import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Check, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { submitInquiry, uploadReference } from "@/lib/api";
import { Seo } from "@/components/Seo";

const EASE = [0.16, 1, 0.3, 1];

const BUILDING_OPTIONS = ["Website", "Landing page", "Web application", "Not sure yet"];
const SITE_OPTIONS = ["Yes — it needs rebuilding", "No — starting fresh"];
const GOAL_OPTIONS = [
  "Generate leads / win more jobs",
  "Take bookings",
  "Sell products or services",
  "Replace manual processes",
  "Customer/client management",
  "Something else",
];

export default function Start() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  // One persistent form object — going Back never resets anything.
  const [form, setForm] = useState({
    building: "",
    has_site: "",
    site_url: "",
    goals: [],
    goals_other: "",
    context: "",
    reference_links: "",
    attachments: [],
    name: "",
    business_name: "",
    email: "",
    phone: "",
  });
  const [uploading, setUploading] = useState(0);
  const fileInputRef = useRef(null);

  // iPhone keyboard: when the visual viewport shrinks, open up scroll room so
  // the active field and the Continue/Send controls stay reachable, and ease
  // the focused field into the visible middle after the keyboard animates.
  const [kbOpen, setKbOpen] = useState(false);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const onResize = () => setKbOpen(vv.height < window.innerHeight * 0.75);
    vv.addEventListener("resize", onResize);
    return () => vv.removeEventListener("resize", onResize);
  }, []);
  const focusScroll = (e) => {
    setTimeout(() => {
      e.target.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 320);
  };

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    for (const f of files) {
      setUploading((n) => n + 1);
      try {
        const { data } = await uploadReference(f);
        setForm((prev) => ({
          ...prev,
          attachments: [...prev.attachments, { path: data.path, filename: data.filename }],
        }));
      } catch (e) {
        toast.error(`Couldn't upload ${f.name}.`);
      } finally {
        setUploading((n) => n - 1);
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeAttachment = (path) =>
    setForm((f) => ({ ...f, attachments: f.attachments.filter((a) => a.path !== path) }));

  const total = 5;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleGoal = (goal) =>
    setForm((f) => ({
      ...f,
      goals: f.goals.includes(goal) ? f.goals.filter((g) => g !== goal) : [...f.goals, goal],
    }));

  const canContinue =
    step === 0 ? !!form.building
    : step === 1 ? !!form.has_site
    : step === 2 ? form.goals.length > 0
    : step === 3 ? true
    : !!form.name.trim() && /.+@.+\..+/.test(form.email);

  const next = () => setStep((s) => Math.min(s + 1, total - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setSubmitting(true);
    try {
      await submitInquiry({
        building: form.building,
        has_site: form.has_site,
        site_url: form.site_url || null,
        goals: form.goals,
        goals_other: form.goals_other || null,
        context: form.context || null,
        reference_links: form.reference_links || null,
        attachments: form.attachments.length ? form.attachments : null,
        name: form.name.trim(),
        business_name: form.business_name.trim() || null,
        email: form.email.trim(),
        phone: form.phone.trim() || null,
      });
      setDone(true);
    } catch (e) {
      toast.error("Something went wrong sending that. Try once more.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="inquiry-success" className="flex min-h-[85vh] flex-col items-center justify-center px-6 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <span className="mx-auto flex h-14 w-14 items-center justify-center border border-white/25">
            <Check className="h-6 w-6" />
          </span>
          <h1 className="mt-8 font-display text-4xl font-medium tracking-[-0.02em] md:text-6xl">
            Received.
          </h1>
          <p className="mx-auto mt-5 max-w-sm text-sm leading-relaxed text-fog md:text-base">
            That&rsquo;s the whole intake — a few sharp questions and it&rsquo;s with us.
            We&rsquo;ll be in touch.
          </p>
          <Link
            to="/"
            data-testid="success-home-link"
            className="mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-fog transition-colors duration-300 hover:text-white"
          >
            Back to the site <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="start-page" className={`flex min-h-screen flex-col px-5 pt-32 transition-[padding] duration-300 md:px-16 md:pt-40 ${kbOpen ? "pb-[48vh]" : "pb-24"}`}>
      <Seo
        title="Start a Project"
        description="Tell us what you're building — a short, guided intake. We read every word and reply within one business day."
        path="/start"
      />
      <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col">
        <p className="text-[10px] font-medium uppercase tracking-[0.5em] text-fog md:text-xs">
          Start a project
        </p>

        <div className="mt-8 flex gap-1.5" data-testid="start-progress">
          {Array.from({ length: total }).map((_, i) => (
            <span
              key={i}
              className={`h-px flex-1 transition-colors duration-700 ${i <= step ? "bg-white" : "bg-white/15"}`}
            />
          ))}
        </div>

        <div className="mt-14 flex-1 md:mt-20">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -40 }}
              transition={{ duration: 0.55, ease: EASE }}
            >
              {step === 0 && (
                <>
                  <h1 className="font-display text-3xl font-medium tracking-[-0.02em] md:text-5xl">
                    What are we building?
                  </h1>
                  <div className="mt-8 flex flex-wrap gap-3">
                    {BUILDING_OPTIONS.map((option, oi) => (
                      <button
                        key={option}
                        type="button"
                        data-testid={`start-option-0-${oi}`}
                        onClick={() => {
                          set("building", option);
                          setTimeout(next, 340);
                        }}
                        className={`border px-5 py-3 text-sm transition-colors duration-400 ease-cinematic md:text-base ${
                          form.building === option
                            ? "border-white bg-white text-obsidian"
                            : "border-white/20 hover:border-white hover:bg-white hover:text-obsidian"
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {step === 1 && (
                <>
                  <h1 className="font-display text-3xl font-medium tracking-[-0.02em] md:text-5xl">
                    Do you already have a website or product?
                  </h1>
                  <div className="mt-8 flex flex-wrap gap-3">
                    {SITE_OPTIONS.map((option, oi) => (
                      <button
                        key={option}
                        type="button"
                        data-testid={`start-option-1-${oi}`}
                        onClick={() => {
                          set("has_site", option);
                          if (option.startsWith("No")) setTimeout(next, 340);
                        }}
                        className={`border px-5 py-3 text-sm transition-colors duration-400 ease-cinematic md:text-base ${
                          form.has_site === option
                            ? "border-white bg-white text-obsidian"
                            : "border-white/20 hover:border-white hover:bg-white hover:text-obsidian"
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                  {form.has_site.startsWith("Yes") && (
                    <motion.input
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, ease: EASE }}
                      type="url"
                      data-testid="start-site-url-input"
                      placeholder="Where does it live? (optional)"
                      value={form.site_url}
                      onChange={(e) => set("site_url", e.target.value)}
                      onFocus={focusScroll}
                      autoComplete="url"
                      className="mt-6 w-full border-b border-white/25 bg-transparent py-3 text-base outline-none transition-colors placeholder:text-fog/70 focus:border-white"
                    />
                  )}
                </>
              )}

              {step === 2 && (
                <>
                  <p className="mb-4 text-[10px] font-medium uppercase tracking-[0.5em] text-fog md:text-xs">
                    Select all that apply
                  </p>
                  <h1 className="font-display text-3xl font-medium tracking-[-0.02em] md:text-5xl">
                    What does it need to accomplish?
                  </h1>
                  <div className="mt-8 flex flex-wrap gap-3">
                    {GOAL_OPTIONS.map((option, oi) => {
                      const selected = form.goals.includes(option);
                      return (
                        <button
                          key={option}
                          type="button"
                          data-testid={`start-goal-${oi}`}
                          aria-pressed={selected}
                          onClick={() => toggleGoal(option)}
                          className={`border px-5 py-3 text-sm transition-colors duration-400 ease-cinematic md:text-base ${
                            selected
                              ? "border-white bg-white text-obsidian"
                              : "border-white/20 hover:border-white hover:bg-white hover:text-obsidian"
                          }`}
                        >
                          {option}
                        </button>
                      );
                    })}
                  </div>
                  <AnimatePresence>
                    {form.goals.includes("Something else") && (
                      <motion.textarea
                        key="goals-other"
                        initial={{ opacity: 0, y: 16, height: 0 }}
                        animate={{ opacity: 1, y: 0, height: "auto" }}
                        exit={{ opacity: 0, y: 16, height: 0 }}
                        transition={{ duration: 0.5, ease: EASE }}
                        data-testid="start-goals-other-input"
                        rows={2}
                        value={form.goals_other}
                        onChange={(e) => set("goals_other", e.target.value)}
                        onFocus={focusScroll}
                        placeholder="Tell us about it — this adds to your selections above."
                        className="mt-6 w-full resize-none border-b border-white/25 bg-transparent py-3 text-base outline-none transition-colors placeholder:text-fog/70 focus:border-white"
                      />
                    )}
                  </AnimatePresence>
                </>
              )}

              {step === 3 && (
                <>
                  <p className="mb-4 text-[10px] font-medium uppercase tracking-[0.5em] text-fog md:text-xs">
                    Optional
                  </p>
                  <h1 className="font-display text-3xl font-medium tracking-[-0.02em] md:text-5xl">
                    Anything else we should know?
                  </h1>
                  <p className="mt-4 text-sm leading-relaxed text-fog md:text-base">
                    Unusual requirements, ideas, problems, functionality — anything the questions
                    so far didn&rsquo;t capture. Skip it if nothing comes to mind.
                  </p>
                  <textarea
                    data-testid="start-context-input"
                    rows={4}
                    value={form.context}
                    onChange={(e) => set("context", e.target.value)}
                    onFocus={focusScroll}
                    placeholder="In your words…"
                    className="mt-8 w-full resize-none border-b border-white/25 bg-transparent py-3 text-base outline-none transition-colors placeholder:text-fog/70 focus:border-white"
                  />
                  <input
                    type="text"
                    data-testid="start-reference-links-input"
                    placeholder="Links to sites you like (optional)"
                    value={form.reference_links}
                    onChange={(e) => set("reference_links", e.target.value)}
                    onFocus={focusScroll}
                    className="mt-6 w-full border-b border-white/25 bg-transparent py-3 text-base outline-none transition-colors placeholder:text-fog/70 focus:border-white"
                  />
                  <div className="mt-6">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                      data-testid="start-file-input"
                      onChange={(e) => onFiles(e.target.files)}
                      className="hidden"
                    />
                    <button
                      type="button"
                      data-testid="start-attach-button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading > 0}
                      className="flex items-center gap-2 border border-dashed border-white/25 px-5 py-3 text-sm text-fog transition-colors duration-400 ease-cinematic hover:border-white hover:text-white disabled:opacity-50"
                    >
                      <Paperclip className="h-4 w-4" />
                      {uploading > 0 ? "Uploading…" : "Attach sketches, logos, or screenshots (optional)"}
                    </button>
                    {form.attachments.length > 0 && (
                      <ul className="mt-4 flex flex-wrap gap-2" data-testid="start-attachment-list">
                        {form.attachments.map((a, i) => (
                          <li
                            key={a.path}
                            className="flex items-center gap-2 border border-white/20 px-3 py-1.5 text-xs text-fog"
                          >
                            <span className="max-w-[180px] truncate">{a.filename}</span>
                            <button
                              type="button"
                              data-testid={`start-attachment-remove-${i}`}
                              aria-label={`Remove ${a.filename}`}
                              onClick={() => removeAttachment(a.path)}
                              className="text-fog transition-colors hover:text-white"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </>
              )}

              {step === 4 && (
                <>
                  <h1 className="font-display text-3xl font-medium tracking-[-0.02em] md:text-5xl">
                    Where do we reply?
                  </h1>
                  <div className="mt-8 flex flex-col gap-6">
                    <input
                      type="text"
                      data-testid="start-name-input"
                      placeholder="Your name"
                      value={form.name}
                      onChange={(e) => set("name", e.target.value)}
                      onFocus={focusScroll}
                      autoComplete="name"
                      className="w-full border-b border-white/25 bg-transparent py-3 text-base outline-none transition-colors placeholder:text-fog/70 focus:border-white"
                    />
                    <input
                      type="text"
                      data-testid="start-business-input"
                      placeholder="Business name"
                      value={form.business_name}
                      onChange={(e) => set("business_name", e.target.value)}
                      onFocus={focusScroll}
                      autoComplete="organization"
                      className="w-full border-b border-white/25 bg-transparent py-3 text-base outline-none transition-colors placeholder:text-fog/70 focus:border-white"
                    />
                    <input
                      type="email"
                      data-testid="start-email-input"
                      placeholder="Email"
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                      onFocus={focusScroll}
                      autoComplete="email"
                      className="w-full border-b border-white/25 bg-transparent py-3 text-base outline-none transition-colors placeholder:text-fog/70 focus:border-white"
                    />
                    <input
                      type="tel"
                      data-testid="start-phone-input"
                      placeholder="Phone (optional)"
                      value={form.phone}
                      onChange={(e) => set("phone", e.target.value)}
                      onFocus={focusScroll}
                      autoComplete="tel"
                      className="w-full border-b border-white/25 bg-transparent py-3 text-base outline-none transition-colors placeholder:text-fog/70 focus:border-white"
                    />
                  </div>
                  <p className="mt-6 text-sm leading-relaxed text-fog/80">
                    This comes straight to the studio. No newsletters, no lists.
                  </p>
                </>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-14 flex items-center justify-between border-t border-white/10 pt-8">
          <button
            type="button"
            data-testid="start-back-button"
            onClick={back}
            disabled={step === 0}
            className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-fog transition-colors duration-300 hover:text-white disabled:pointer-events-none disabled:opacity-30"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>

          {step < total - 1 ? (
            <button
              type="button"
              data-testid="start-next-button"
              onClick={next}
              disabled={!canContinue}
              className="group flex items-center gap-2 border border-white px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] transition-colors duration-500 ease-cinematic hover:bg-white hover:text-obsidian disabled:pointer-events-none disabled:opacity-30"
            >
              Continue
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-500 ease-cinematic group-hover:translate-x-0.5" />
            </button>
          ) : (
            <button
              type="button"
              data-testid="start-submit-button"
              onClick={submit}
              disabled={!canContinue || submitting}
              className="group flex items-center gap-2 border border-white bg-white px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-obsidian transition-colors duration-500 ease-cinematic hover:bg-transparent hover:text-white disabled:pointer-events-none disabled:opacity-30"
            >
              {submitting ? "Sending…" : "Send it"}
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-500 ease-cinematic group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
